import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { crmApi, errMsg } from '@crm/api';
import { UiAutosuggest, UiButton, UiDateRangePicker } from '@shared/common/ui';

const SYSTEM_TYPE_LABEL = {
  lead: 'Новый / лид',
  trial: 'Пробный этап',
  active: 'Активный',
  paused: 'Пауза',
  left: 'Отток',
  archive: 'Архив',
};

function monthStart() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

function num(value) {
  return value === null || value === undefined ? '—' : value;
}

function Metric({ label, value, hint }) {
  return (
    <div className="sales-funnel__metric">
      <span className="sales-funnel__metric-value">{value}</span>
      <span className="sales-funnel__metric-label">{label}</span>
      {hint && <span className="sales-funnel__metric-hint">{hint}</span>}
    </div>
  );
}

function DurationCells({ stat }) {
  return (
    <>
      <td>{num(stat?.avgDays)}</td>
      <td>{num(stat?.medianDays)}</td>
      <td className="muted">{stat?.count ?? 0}</td>
    </>
  );
}

/**
 * Две методики считаются и подписываются раздельно, чтобы цифры нельзя было
 * прочитать двусмысленно: когорта отвечает «как конвертировались пришедшие в периоде»,
 * события — «сколько переходов произошло в периоде».
 */
export function SalesFunnelAnalytics() {
  const { branchId } = useParams();
  const [range, setRange] = useState({ from: monthStart(), to: today() });
  const [direction, setDirection] = useState(null);
  const [dirQuery, setDirQuery] = useState('');
  const [dirSuggestions, setDirSuggestions] = useState([]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [applied, setApplied] = useState({ from: monthStart(), to: today(), directionId: null });

  const fetchData = useCallback(async () => {
    if (!branchId || !applied.from || !applied.to) return;
    setLoading(true);
    setError(null);
    const params = { from: applied.from, to: applied.to };
    if (applied.directionId) params.directionId = applied.directionId;
    try {
      const res = await crmApi(branchId).get('/funnel/analytics', { params });
      setData(res.data);
    } catch (e) {
      setError(errMsg(e, 'Не удалось загрузить аналитику'));
    } finally {
      setLoading(false);
    }
  }, [branchId, applied]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!dirQuery.trim() || !branchId) {
      setDirSuggestions([]);
      return undefined;
    }
    const t = setTimeout(() => {
      crmApi(branchId)
        .get('/directions', { params: { q: dirQuery.trim(), limit: 30 } })
        .then(({ data: res }) => setDirSuggestions(res.items ?? []))
        .catch(() => setDirSuggestions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [dirQuery, branchId]);

  const apply = () =>
    setApplied({ from: range.from, to: range.to, directionId: direction?.id ?? null });

  const cohort = data?.cohort;
  const events = data?.events;

  return (
    <div className="sales-funnel">
      <div className="sales-funnel__filters">
        <UiDateRangePicker
          label="Период"
          from={range.from}
          to={range.to}
          onChange={(v) => setRange(v)}
        />
        <UiAutosuggest
          label="Направление"
          value={direction ? direction.name : dirQuery}
          onChange={(v) => {
            setDirQuery(v);
            setDirection(null);
          }}
          suggestions={dirSuggestions}
          getOptionLabel={(it) => it.name}
          getOptionKey={(it) => it.id}
          onSelect={(it) => {
            setDirection(it);
            setDirQuery('');
          }}
          clearable
          onClear={() => {
            setDirection(null);
            setDirQuery('');
          }}
          placeholder="Все направления"
        />
        <UiButton type="button" onClick={apply}>
          Показать
        </UiButton>
      </div>

      {error && <div className="banner banner--danger">{error}</div>}
      {loading && <div className="placeholder">Загрузка…</div>}

      {!loading && data && (
        <>
          <section className="sales-funnel__section">
            <h2>Когорта: участия, начавшиеся в периоде</h2>
            <p className="muted">
              Отвечает на вопрос «как конвертировались те, кто пришёл в выбранном периоде».
              Каждое направление клиента считается отдельной траекторией.
            </p>

            <div className="sales-funnel__metrics">
              <Metric label="Новых участий" value={cohort.started} />
              <Metric
                label="Дошли до пробного"
                value={cohort.conversions.leadToTrial.count}
                hint={`${cohort.conversions.leadToTrial.rate}% от новых`}
              />
              <Metric
                label="Купили после пробного"
                value={cohort.conversions.trialToActive.count}
                hint={`${cohort.conversions.trialToActive.rate}% от дошедших до пробного`}
              />
              <Metric
                label="Дошли до активных"
                value={cohort.conversions.leadToActive.count}
                hint={`${cohort.conversions.leadToActive.rate}% от новых`}
              />
            </div>

            <h3>Сколько участий достигали каждого системного этапа</h3>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Системный этап</th>
                  <th style={{ width: 120 }}>Участий</th>
                  <th style={{ width: 120 }}>Доля</th>
                </tr>
              </thead>
              <tbody>
                {cohort.reached.map((it) => (
                  <tr key={it.systemType}>
                    <td>{SYSTEM_TYPE_LABEL[it.systemType] ?? it.systemType}</td>
                    <td>{it.count}</td>
                    <td className="muted">{it.rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h3>Время на переход, дней</h3>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Переход</th>
                  <th style={{ width: 120 }}>Среднее</th>
                  <th style={{ width: 120 }}>Медиана</th>
                  <th style={{ width: 120 }}>Наблюдений</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Лид → пробное</td>
                  <DurationCells stat={cohort.timings.leadToTrial} />
                </tr>
                <tr>
                  <td>Пробное → активный</td>
                  <DurationCells stat={cohort.timings.trialToActive} />
                </tr>
                <tr>
                  <td>Лид → активный</td>
                  <DurationCells stat={cohort.timings.leadToActive} />
                </tr>
              </tbody>
            </table>
          </section>

          <section className="sales-funnel__section">
            <h2>События: переходы, случившиеся в периоде</h2>
            <p className="muted">
              Отвечает на вопрос «сколько переходов произошло в периоде», независимо от того,
              когда участие началось.
            </p>

            <div className="sales-funnel__metrics">
              <Metric label="Всего переходов" value={events.total} />
              <Metric label="Переходов в отток" value={events.leftCount} />
            </div>

            <h3>По системным типам</h3>
            {events.systemTransitions.length === 0 ? (
              <div className="placeholder">Переходов не было</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Из</th>
                    <th>В</th>
                    <th style={{ width: 120 }}>Переходов</th>
                  </tr>
                </thead>
                <tbody>
                  {events.systemTransitions.map((it) => (
                    <tr key={`${it.from ?? '-'}>${it.to ?? '-'}`}>
                      <td>{SYSTEM_TYPE_LABEL[it.from] ?? '—'}</td>
                      <td>{SYSTEM_TYPE_LABEL[it.to] ?? '—'}</td>
                      <td>{it.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <h3>Детализация по конкретным статусам</h3>
            {events.transitions.length === 0 ? (
              <div className="placeholder">Переходов не было</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Из</th>
                    <th>В</th>
                    <th style={{ width: 120 }}>Переходов</th>
                  </tr>
                </thead>
                <tbody>
                  {events.transitions.map((it) => (
                    <tr key={`${it.from ?? '-'}>${it.to ?? '-'}`}>
                      <td>
                        {it.fromName ?? '— начало участия —'}
                        {it.fromSystemType && (
                          <span className="muted"> · {SYSTEM_TYPE_LABEL[it.fromSystemType]}</span>
                        )}
                      </td>
                      <td>
                        {it.toName ?? '—'}
                        {it.toSystemType && (
                          <span className="muted"> · {SYSTEM_TYPE_LABEL[it.toSystemType]}</span>
                        )}
                      </td>
                      <td>{it.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="sales-funnel__section">
            <h2>Сколько держится статус, дней</h2>
            <p className="muted">
              Считается по закрытым отрезкам: от перехода в статус до следующего перехода.
            </p>
            {data.statusDwell.length === 0 ? (
              <div className="placeholder">Данных пока нет</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Статус</th>
                    <th>Системный тип</th>
                    <th style={{ width: 120 }}>Среднее</th>
                    <th style={{ width: 120 }}>Медиана</th>
                    <th style={{ width: 120 }}>Наблюдений</th>
                  </tr>
                </thead>
                <tbody>
                  {data.statusDwell.map((it) => (
                    <tr key={it.statusKey}>
                      <td>{it.name}</td>
                      <td className="muted">{SYSTEM_TYPE_LABEL[it.systemType] ?? '—'}</td>
                      <DurationCells stat={it.stat} />
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}
